import React, { useEffect, useMemo, useRef, useState } from "react";
import { CompanyAdminLayout } from "../../../components/CompanyAdmin/CompanyAdminLayout";
import { Button } from "../../../components/UI/Button";
import NotificationsList from "../../../components/UI/NotificationList";
import {
  companyPluginUseCase,
  companyShopUseCase,
  companyUseCase,
  contentUseCase,
  shopProductUseCase,
} from "../../../../di/container";
import { useAuthStore } from "../../../stores/auth-store";
import { useNotifications } from "../../../hooks/use-notifications";
import type {
  ShopBannerEntity,
  ShopEntity,
  ShopFooterDesign,
  ShopHeaderDesign,
  ShopSectionEntity,
  ShopSectionPortfolio,
  UpdateShopSectionRequest,
} from "../../../../domain/types/company-shop";
import type { ProductLightDto } from "../../../../domain/types/shop";
import type { ProductEntity } from "../../../../domain/types/product";
import type { CompanyInfoResponse } from "../../../../domain/types/shared";
import type { LiveConsultPluginConfig } from "../../../../domain/types/company-plugin";

import { BestSellerModal } from "./components/modals/BestSellerModal";
import { NewBannerForm } from "./components/modals/NewBannerForm";
import { EditBannerForm } from "./components/modals/EditBannerForm";

import { HeaderSettings } from "./components/settings/HeaderSettings";
import { MainSectionsSettings } from "./components/settings/MainSectionsSettings";
import { OrderSettings } from "./components/settings/OrderSettings";
import { FooterSettings } from "./components/settings/FooterSettings";

import { ShopLivePreview } from "./components/preview/ShopLivePreview";
import type {
  PreviewDevice,
  PreviewPage,
  PreviewScrollRequest,
} from "./components/preview/ShopLivePreview";
import { PreviewHeader } from "./components/preview/PreviewHeader";
import { PreviewBanner } from "./components/preview/PreviewBanner";
import { PreviewStripBanner } from "./components/preview/PreviewStripBanner";
import { PreviewRecentlyViewed } from "./components/preview/PreviewRecentlyViewed";
import { PreviewBestSeller } from "./components/preview/PreviewBestSeller";
import { PreviewAllProducts } from "./components/preview/PreviewAllProducts";
import { PreviewProductList } from "./components/preview/PreviewProductList";
import { PreviewPortfolio } from "./components/preview/PreviewPortfolio";
import { PreviewConsultFloating } from "./components/preview/PreviewConsultFloating";
import { PreviewFooter } from "./components/preview/PreviewFooter";

import { DEFAULT_FOOTER_DESIGN, DEFAULT_HEADER_DESIGN } from "./constants";
import {
  buildFallbackSections,
  buildSaveResultMessage,
  isFallbackSection,
  normalizeConsultEnabled,
  resolveErrorMessage,
  runSaveSteps,
  SECTION_TYPE_TO_SHOP_FLAG,
  syncSectionsWithShopFlags,
} from "./utils";
import type { SaveStep } from "./utils";

/*
  쇼핑몰 디자인 화면 (2026-10-26 개편).

  화면 구성
  - 왼쪽: 실제 쇼핑몰 미리보기. 쇼핑몰 서버가 저장 전 설정으로 진짜 템플릿을 다시 그린다(ShopLivePreview).
  - 오른쪽: 쇼핑몰 화면 위치 기준 탭 4개(헤더 · 메인 화면 · 주문 · 상담 · 푸터)와 하단 저장 바.

  저장
  예전에는 카드마다 저장 버튼이 따로 있고(헤더 · 푸터 · 섹션 순서 · 전체 상품 · 포트폴리오 · 상품 리스트 · 띠배너)
  나머지는 맨 아래 [저장]으로만 반영돼서, 무엇이 저장됐는지 알기 어려웠다. 이제 저장 버튼은 하나다.
  바뀐 것만 아래 API 로 차례로 보낸다. 백엔드에 이들을 묶는 트랜잭션이 없어서 롤백은 하지 않고,
  실패하면 어느 단계에서 멈췄는지와 이미 저장된 단계를 알린다(runSaveSteps).

  | 단계          | API                                         |
  |---------------|---------------------------------------------|
  | 헤더          | PATCH /shop/header                          |
  | 푸터          | PATCH /shop/footer                          |
  | 섹션 n개      | PUT /shop/section/{id}                      |
  | 섹션 순서     | PUT /shop/section/order                     |
  | 쇼핑몰 설정   | PATCH /shop/setting                         |
  | 베스트셀러    | PUT /shop/bestseller                        |
  | 띠배너        | PUT /shop/strip-banner (이미지 업로드 포함) |

  바로 반영되는 동작(창에서 저장): 배너 추가 · 수정, 상품 리스트 섹션 추가 · 삭제, 띠배너 이미지 삭제.
*/

type SettingsTab = "HEADER" | "MAIN" | "ORDER" | "FOOTER";

const SETTINGS_TABS: { key: SettingsTab; label: string }[] = [
  { key: "HEADER", label: "헤더" },
  { key: "MAIN", label: "메인 화면" },
  { key: "ORDER", label: "주문 · 상담" },
  { key: "FOOTER", label: "푸터" },
];

/** PATCH /shop/setting 이 저장하는 값 (탭별). 띠배너 값은 띠배너 API 가 따로 저장한다 */
const SHOP_SETTING_FIELDS_BY_TAB: Record<SettingsTab, (keyof ShopEntity)[]> = {
  HEADER: [],
  MAIN: [
    "isCarouselLayout",
    "designProductSlideInterval",
    "recentlyViewedEnabled",
    "bestSellerEnabled",
  ],
  ORDER: ["productDetailLayout", "myDesignEnabled", "consultEnabled"],
  FOOTER: [
    "instagramUrl",
    "facebookUrl",
    "youtubeUrl",
    "naverBlogUrl",
    "kakaoChannelUrl",
  ],
};
const STRIP_BANNER_FIELDS: (keyof ShopEntity)[] = [
  "stripBannerEnabled",
  "stripBannerLink",
];

const snapshot = (shop: ShopEntity | null, fields: (keyof ShopEntity)[]) =>
  shop ? JSON.stringify(fields.map((field) => shop[field] ?? null)) : "";

const bestSellerIdsOf = (products: ProductEntity[] | null) =>
  (products ?? [])
    .filter((product) => product.isBestSeller)
    .map((product) => product.id);

/**
 * 섹션 하나를 저장할 때 보내는 값. 타입마다 바꿀 수 있는 필드가 다르다.
 * 띠배너 · 최근 둘러본 · 베스트셀러의 사용 여부는 shops 불리언(쇼핑몰 설정)이 단일 진실이라 여기서 보내지 않는다.
 */
const sectionPayload = (
  section: ShopSectionEntity,
): UpdateShopSectionRequest | null => {
  switch (section.sectionType) {
    case "BANNER":
      return { isEnabled: section.isEnabled };
    case "ALL_PRODUCTS":
      return {
        title: section.title,
        subtitle: section.subtitle,
        columnCount: section.columnCount,
        isEnabled: section.isEnabled,
      };
    case "PORTFOLIO":
      // LIST 일 때도 열 수를 보낸다. GRID 로 되돌리면 고르던 열 수가 그대로 복원된다
      return {
        title: section.title,
        subtitle: section.subtitle,
        layoutType: section.layoutType,
        columnCount: section.columnCount,
        isEnabled: section.isEnabled,
      };
    case "PRODUCT_LIST":
      return {
        title: section.title,
        subtitle: section.subtitle,
        cardShape: section.cardShape,
        columnCount: section.columnCount,
        isEnabled: section.isEnabled,
        // 배열 순서가 곧 display_order 다
        productIds: section.products.map((product) => product.id),
      };
    default:
      return null;
  }
};

/** 회사 도메인 → 쇼핑몰 주소. localhost 는 개발 서버라 http 다 */
const toShopBaseUrl = (domain: string | null | undefined): string | null => {
  // 개발 전용: 쇼핑몰을 다른 포트로 띄워 볼 때 localStorage "pod24:shopPreviewBaseUrl" 로 주소를 바꾼다
  if (process.env.NODE_ENV === "development") {
    const override = window.localStorage.getItem("pod24:shopPreviewBaseUrl");
    if (override) return override.replace(/\/+$/, "");
  }
  const value = domain?.trim().replace(/\/+$/, "");
  if (!value) return null;
  if (/^https?:\/\//.test(value)) return value;
  return /^(localhost|127\.0\.0\.1)(:\d+)?$/.test(value)
    ? `http://${value}`
    : `https://${value}`;
};

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const MallDesign = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? null;
  const defaultLogoUrl = "/assets/logo/simbol_logo.png";
  const { notifications, addItem: addNotificationItem } = useNotifications();

  const [companyInfo, setCompanyInfo] = useState<CompanyInfoResponse>();
  const [activeTab, setActiveTab] = useState<SettingsTab>("HEADER");
  const [expandedSectionId, setExpandedSectionId] = useState<number | null>(
    null,
  );
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("desktop");
  const [previewPage, setPreviewPage] = useState<PreviewPage>("main");
  const [scrollRequest, setScrollRequest] =
    useState<PreviewScrollRequest | null>(null);
  const settingsScrollRef = useRef<HTMLDivElement>(null);

  /* ---------------------------- 편집 중인 값과 마지막 저장값 ---------------------------- */
  const [shopEntityData, setShopEntityData] = useState<ShopEntity | null>(null);
  const [savedShop, setSavedShop] = useState<ShopEntity | null>(null);
  const [headerDesign, setHeaderDesign] = useState<ShopHeaderDesign>(
    DEFAULT_HEADER_DESIGN,
  );
  const [savedHeader, setSavedHeader] = useState<ShopHeaderDesign>(
    DEFAULT_HEADER_DESIGN,
  );
  const [footerDesign, setFooterDesign] = useState<ShopFooterDesign>(
    DEFAULT_FOOTER_DESIGN,
  );
  const [savedFooter, setSavedFooter] = useState<ShopFooterDesign>(
    DEFAULT_FOOTER_DESIGN,
  );
  /*
    섹션. 백엔드 배포 전(GET /shop/section 없음)에는 계약의 seed 순서로 합성해 화면은 같게 그리고 저장은 막는다.
  */
  const [sections, setSections] = useState<ShopSectionEntity[]>(() =>
    buildFallbackSections(null),
  );
  const [savedSections, setSavedSections] = useState<ShopSectionEntity[]>([]);
  const [isSectionsPersistable, setIsSectionsPersistable] = useState(false);
  const [productsData, setProductsData] = useState<ProductEntity[] | null>(
    null,
  );
  const [savedBestSellerIds, setSavedBestSellerIds] = useState<number[]>([]);
  const [stripBannerFile, setStripBannerFile] = useState<File | null>(null);
  const [stripBannerPreviewUrl, setStripBannerPreviewUrl] = useState<
    string | null
  >(null);
  const [stripBannerDataUrl, setStripBannerDataUrl] = useState<string | null>(
    null,
  );

  /* ---------------------------- 화면 보조 데이터 ---------------------------- */
  const [bannersData, setBannersData] = useState<ShopBannerEntity[] | null>(
    null,
  );
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState<
    ProductEntity[] | null
  >(null);
  const [products, setProducts] = useState<ProductLightDto[] | null>(null);
  const [publishedPortfolios, setPublishedPortfolios] = useState<
    ShopSectionPortfolio[]
  >([]);
  /** 아직 못 읽었으면 null. 폴백 섹션 합성은 이 값이 정해진 뒤에 한다 */
  const [portfolioMenuEnabled, setPortfolioMenuEnabled] = useState<
    boolean | null
  >(null);
  /*
    실시간 상담 플러그인 (내 회사 정보 > 추가 서비스 연동). 이 화면은 ON/OFF 만 고르고
    채널/링크/키는 플러그인이 가진다. undefined = 아직 불러오는 중, null = 미등록.
  */
  const [liveConsult, setLiveConsult] = useState<
    LiveConsultPluginConfig | null | undefined
  >(undefined);

  const [isBestSellerModalVisible, setIsBestSellerModalVisible] =
    useState(false);
  const [isNewBannerModalVisible, setIsNewBannerModalVisible] = useState(false);
  const [isEditBannerModalVisible, setIsEditBannerModalVisible] =
    useState(false);
  const [editingBannerId, setEditingBannerId] = useState<number | null>(null);
  const [bestSellerCount, setBestSellerCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  /* ---------------------------- 불러오기 ---------------------------- */
  useEffect(() => {
    if (!companyId) return;
    companyUseCase.getCompanyInfo({ companyId }).then(async (company) => {
      setCompanyInfo(company);
      if (!company.shopId) return;
      setRecentlyViewedProducts(
        await shopProductUseCase.getRecentlyViewedProducts(company.shopId),
      );
      setProducts(await shopProductUseCase.getProductList(company.shopId));
    });
    companyPluginUseCase
      .getLiveConsult(companyId)
      .then(setLiveConsult)
      // 목록을 못 가져와도 화면은 그려야 한다. 미등록으로 보고 안내만 띄운다.
      .catch(() => setLiveConsult(null));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    companyShopUseCase.getShopHome(companyId).then((home) => {
      const shop = normalizeConsultEnabled(home.shop);
      setShopEntityData(shop);
      setSavedShop(shop);
      if (home.banners) setBannersData(home.banners);
      if (home.products) {
        setProductsData(home.products);
        setSavedBestSellerIds(bestSellerIdsOf(home.products));
      }
      // 백엔드가 아직 안 내려주면 프리셋 기본값을 그대로 쓴다
      if (home.headerDesign) {
        setHeaderDesign(home.headerDesign);
        setSavedHeader(home.headerDesign);
      }
      if (home.footerDesign) {
        setFooterDesign(home.footerDesign);
        setSavedFooter(home.footerDesign);
      }
    });
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      contentUseCase.getPortfolios(companyId),
      contentUseCase.getPortfolioSection(companyId),
    ])
      .then(([portfolios, portfolioSetting]) => {
        setPublishedPortfolios(
          portfolios
            .filter((portfolio) => portfolio.isPublished)
            .map((portfolio) => ({
              id: portfolio.id,
              title: portfolio.title,
              summary: portfolio.summary,
              thumbnailUrl: portfolio.thumbnailUrl,
              clientName: portfolio.clientName,
              workedAt: portfolio.workedAt,
            })),
        );
        setPortfolioMenuEnabled(portfolioSetting.isEnabled);
      })
      .catch((error) => {
        // 미리보기 보조 데이터일 뿐이라 실패해도 나머지 화면은 그대로 동작한다
        console.error(error);
        setPortfolioMenuEnabled(false);
      });
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    companyShopUseCase
      .getShopSections(companyId)
      .then((fetched) => {
        setSections(fetched);
        setSavedSections(fetched);
        setIsSectionsPersistable(true);
      })
      .catch((error) => {
        // 백엔드 미배포 구간. 합성 섹션으로 계속 동작하되 저장은 막는다.
        console.error(error);
        setIsSectionsPersistable(false);
      });
  }, [companyId]);

  /*
    폴백 섹션은 portfolioMenuEnabled 가 정해지면 한 번만 다시 합성한다.
    매번 다시 합성하면 관리자가 바꿔 둔 순서가 다른 상태 변화 때마다 초기화된다.
  */
  const hasSeededFallbackRef = useRef(false);
  useEffect(() => {
    if (
      isSectionsPersistable ||
      portfolioMenuEnabled === null ||
      hasSeededFallbackRef.current
    )
      return;
    hasSeededFallbackRef.current = true;
    const seeded = buildFallbackSections(shopEntityData, portfolioMenuEnabled);
    setSections(seeded);
    setSavedSections(seeded);
  }, [isSectionsPersistable, shopEntityData, portfolioMenuEnabled]);

  const reloadBannerData = async () => {
    if (!companyId) return;
    const home = await companyShopUseCase.getShopHome(companyId);
    if (home.banners) setBannersData(home.banners);
  };

  /* ---------------------------- 파생 값 ---------------------------- */

  /*
    띠배너 · 최근 둘러본 · 베스트셀러의 on/off 는 shopEntityData 불리언이 단일 진실이고 섹션 값은 여기서 파생한다.
    나머지(배너 · 전체 상품 · 포트폴리오 · 상품 리스트)는 섹션이 진실이다.
  */
  const effectiveSections = useMemo(
    () => syncSectionsWithShopFlags(sections, shopEntityData),
    [sections, shopEntityData],
  );

  /** 미리보기는 저장 전 상담 ON/OFF 를 바로 보여야 하므로 플러그인과 다시 계산한다 */
  const previewShopEntityData = useMemo<ShopEntity | null>(() => {
    if (!shopEntityData) return null;
    const consultChannelType =
      shopEntityData.consultEnabled && liveConsult
        ? liveConsult.consultChannelType
        : "NONE";
    return { ...shopEntityData, consultChannelType };
  }, [shopEntityData, liveConsult]);

  const savedSectionById = useMemo(
    () => new Map(savedSections.map((section) => [section.id, section])),
    [savedSections],
  );

  const dirtySections = useMemo(
    () =>
      sections.filter((section) => {
        const saved = savedSectionById.get(section.id);
        return (
          !!saved &&
          JSON.stringify(sectionPayload(section)) !==
            JSON.stringify(sectionPayload(saved))
        );
      }),
    [sections, savedSectionById],
  );
  const isSectionOrderDirty =
    sections.map((section) => section.id).join(",") !==
    savedSections.map((section) => section.id).join(",");
  const isHeaderDirty =
    JSON.stringify(headerDesign) !== JSON.stringify(savedHeader);
  const isFooterDirty =
    JSON.stringify(footerDesign) !== JSON.stringify(savedFooter);
  const isBestSellerDirty =
    bestSellerIdsOf(productsData).join(",") !== savedBestSellerIds.join(",");
  const isStripBannerDirty =
    !!stripBannerFile ||
    snapshot(shopEntityData, STRIP_BANNER_FIELDS) !==
      snapshot(savedShop, STRIP_BANNER_FIELDS);
  const isShopFieldDirty = (tab: SettingsTab) =>
    snapshot(shopEntityData, SHOP_SETTING_FIELDS_BY_TAB[tab]) !==
    snapshot(savedShop, SHOP_SETTING_FIELDS_BY_TAB[tab]);
  const isShopSettingsDirty = SETTINGS_TABS.some(({ key }) =>
    isShopFieldDirty(key),
  );

  const unsavedTabs = new Set<SettingsTab>();
  if (isHeaderDirty) unsavedTabs.add("HEADER");
  if (
    (isSectionsPersistable &&
      (dirtySections.length > 0 || isSectionOrderDirty)) ||
    isShopFieldDirty("MAIN") ||
    isBestSellerDirty ||
    isStripBannerDirty
  ) {
    unsavedTabs.add("MAIN");
  }
  if (isShopFieldDirty("ORDER")) unsavedTabs.add("ORDER");
  if (isFooterDirty || isShopFieldDirty("FOOTER")) unsavedTabs.add("FOOTER");
  const hasUnsavedChanges = unsavedTabs.size > 0;

  // 저장하지 않고 페이지를 닫으면 브라우저가 한 번 묻는다
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  /* ---------------------------- 편집 동작 ---------------------------- */

  const patchSection = (sectionId: number, patch: Partial<ShopSectionEntity>) =>
    setSections((previous) =>
      previous.map((section) =>
        section.id === sectionId ? { ...section, ...patch } : section,
      ),
    );

  const toggleSectionEnabled = (section: ShopSectionEntity) => {
    const flag = SECTION_TYPE_TO_SHOP_FLAG[section.sectionType];
    if (flag) {
      setShopEntityData((original) =>
        original ? { ...original, [flag]: !original[flag] } : original,
      );
      return;
    }
    patchSection(section.id, { isEnabled: !section.isEnabled });
  };

  const showTab = (tab: SettingsTab) => {
    setActiveTab(tab);
    settingsScrollRef.current?.scrollTo({ top: 0 });
    setPreviewPage(tab === "ORDER" ? "product" : "main");
    setScrollRequest({
      target: tab === "FOOTER" ? "footer" : "top",
      nonce: Date.now(),
    });
  };

  const expandSection = (sectionId: number | null) => {
    setExpandedSectionId(sectionId);
    if (sectionId !== null) {
      setPreviewPage("main");
      setScrollRequest({ target: "section", sectionId, nonce: Date.now() });
    }
  };

  const pickStripBanner = async (file: File) => {
    setStripBannerFile(file);
    setStripBannerPreviewUrl(URL.createObjectURL(file));
    setStripBannerDataUrl(await readAsDataUrl(file).catch(() => null));
    setShopEntityData((original) =>
      original ? { ...original, stripBannerEnabled: true } : original,
    );
  };

  const clearPendingStripBanner = () => {
    setStripBannerFile(null);
    setStripBannerPreviewUrl(null);
    setStripBannerDataUrl(null);
  };

  /** 띠배너 이미지 삭제는 바로 반영된다. 다른 저장 전 값은 건드리지 않고 띠배너 값만 맞춘다 */
  const deleteStripBanner = async () => {
    if (!companyId) return;
    try {
      const result = await companyShopUseCase.deleteStripBanner(companyId);
      const pickStrip = (shop: ShopEntity | null): ShopEntity | null =>
        shop
          ? {
              ...shop,
              stripBannerUrl: result.shop.stripBannerUrl,
              stripBannerEnabled: result.shop.stripBannerEnabled,
              stripBannerLink: result.shop.stripBannerLink,
            }
          : shop;
      setShopEntityData(pickStrip);
      setSavedShop(pickStrip);
      clearPendingStripBanner();
      addNotificationItem("띠배너 이미지가 삭제되었습니다.");
    } catch (error) {
      console.error(error);
      addNotificationItem(
        resolveErrorMessage(error, "띠배너 삭제에 실패했습니다."),
      );
    }
  };

  /** 상품 리스트 추가 · 삭제는 바로 반영된다. 다른 섹션의 저장 전 값은 그대로 둔다 */
  const createProductListSection = async () => {
    if (!companyId || !isSectionsPersistable) return;
    try {
      await companyShopUseCase.createShopSection(companyId, {
        sectionType: "PRODUCT_LIST",
        title: "새 상품 리스트",
        subtitle: null,
        cardShape: "DEFAULT",
        columnCount: 4,
        isEnabled: true,
        productIds: [],
      });
      // displayOrder 는 백엔드가 정하므로 다시 읽고, 새로 생긴 섹션만 붙인다
      const fetched = await companyShopUseCase.getShopSections(companyId);
      const known = new Set(sections.map((section) => section.id));
      const added = fetched.filter((section) => !known.has(section.id));
      setSections((previous) => [...previous, ...added]);
      setSavedSections((previous) => [...previous, ...added]);
      if (added[0]) setExpandedSectionId(added[0].id);
      addNotificationItem("상품 리스트 섹션이 추가되었습니다.");
    } catch (error) {
      console.error(error);
      addNotificationItem(
        resolveErrorMessage(error, "상품 리스트 섹션 추가에 실패했습니다."),
      );
    }
  };

  const deleteProductListSection = async (section: ShopSectionEntity) => {
    if (!companyId || !isSectionsPersistable) return;
    try {
      await companyShopUseCase.deleteShopSection(companyId, section.id);
      setSections((previous) =>
        previous.filter((item) => item.id !== section.id),
      );
      setSavedSections((previous) =>
        previous.filter((item) => item.id !== section.id),
      );
      addNotificationItem("상품 리스트 섹션이 삭제되었습니다.");
    } catch (error) {
      console.error(error);
      addNotificationItem(
        resolveErrorMessage(error, "상품 리스트 섹션 삭제에 실패했습니다."),
      );
    }
  };

  /* ---------------------------- 저장 · 되돌리기 ---------------------------- */

  const buildSaveSteps = (): SaveStep[] => {
    if (!companyId) return [];
    const steps: SaveStep[] = [];

    if (isHeaderDirty) {
      steps.push({
        label: "헤더",
        run: async () => {
          const saved = await companyShopUseCase.updateShopHeaderDesign(
            companyId,
            headerDesign,
          );
          setHeaderDesign(saved);
          setSavedHeader(saved);
        },
      });
    }

    if (isFooterDirty) {
      steps.push({
        label: "푸터",
        run: async () => {
          const saved = await companyShopUseCase.updateShopFooterDesign(
            companyId,
            footerDesign,
          );
          setFooterDesign(saved);
          setSavedFooter(saved);
        },
      });
    }

    if (isSectionsPersistable) {
      for (const section of dirtySections) {
        const payload = sectionPayload(section);
        if (!payload || isFallbackSection(section)) continue;
        steps.push({
          label: `${section.sectionType === "PRODUCT_LIST" ? section.title || "상품 리스트" : "섹션"} 설정`,
          run: async () => {
            const saved = await companyShopUseCase.updateShopSection(
              companyId,
              section.id,
              payload,
            );
            setSections((previous) =>
              previous.map((item) =>
                item.id === saved.id ? { ...item, ...saved } : item,
              ),
            );
            setSavedSections((previous) =>
              previous.map((item) => (item.id === saved.id ? saved : item)),
            );
          },
        });
      }
      if (isSectionOrderDirty) {
        const orderedIds = sections.map((section) => section.id);
        steps.push({
          label: "섹션 순서",
          run: async () => {
            // 해당 쇼핑몰의 모든 섹션이 빠짐없이 들어가야 한다
            await companyShopUseCase.updateShopSectionOrder(companyId, {
              orders: orderedIds.map((sectionId, index) => ({
                sectionId,
                displayOrder: index,
              })),
            });
            setSavedSections((previous) =>
              orderedIds
                .map((id, index) => {
                  const saved = previous.find((section) => section.id === id);
                  return saved ? { ...saved, displayOrder: index } : null;
                })
                .filter(
                  (section): section is ShopSectionEntity => section !== null,
                ),
            );
          },
        });
      }
    }

    if (isShopSettingsDirty && shopEntityData) {
      const current = shopEntityData;
      steps.push({
        label: "쇼핑몰 설정",
        run: async () => {
          await companyShopUseCase.updateShopSettings(companyId, {
            isCarouselLayout: current.isCarouselLayout,
            designProductSlideInterval: current.designProductSlideInterval,
            bestSellerEnabled: current.bestSellerEnabled,
            recentlyViewedEnabled: current.recentlyViewedEnabled,
            instagramUrl: current.instagramUrl,
            facebookUrl: current.facebookUrl,
            youtubeUrl: current.youtubeUrl,
            naverBlogUrl: current.naverBlogUrl,
            kakaoChannelUrl: current.kakaoChannelUrl,
            consultEnabled: current.consultEnabled ?? false,
            productDetailLayout: current.productDetailLayout,
            myDesignEnabled: current.myDesignEnabled,
          });
          // 띠배너 값은 띠배너 단계가 끝나야 저장된 것으로 본다
          setSavedShop((previous) => ({
            ...current,
            stripBannerEnabled:
              previous?.stripBannerEnabled ?? current.stripBannerEnabled,
            stripBannerLink:
              previous?.stripBannerLink ?? current.stripBannerLink,
            stripBannerUrl: previous?.stripBannerUrl ?? current.stripBannerUrl,
          }));
        },
      });
    }

    if (isBestSellerDirty) {
      const ids = bestSellerIdsOf(productsData);
      steps.push({
        label: "베스트셀러",
        run: async () => {
          await companyShopUseCase.updateBestsellers(companyId, {
            productIds: ids,
          });
          setSavedBestSellerIds(ids);
        },
      });
    }

    if (isStripBannerDirty && shopEntityData) {
      const current = shopEntityData;
      const file = stripBannerFile;
      steps.push({
        label: "띠배너",
        run: async () => {
          const result = await companyShopUseCase.updateStripBanner({
            companyId,
            bannerFile: file || undefined,
            command: {
              enabled: current.stripBannerEnabled,
              link: current.stripBannerLink,
            },
          });
          // 업로드된 이미지 주소(presigned URL)만 받아 오고, 다른 저장 전 값은 그대로 둔다
          const strip = {
            stripBannerUrl: result.shop.stripBannerUrl,
            stripBannerEnabled: result.shop.stripBannerEnabled,
            stripBannerLink: result.shop.stripBannerLink,
          };
          setShopEntityData((original) =>
            original ? { ...original, ...strip } : original,
          );
          setSavedShop((previous) =>
            previous ? { ...previous, ...strip } : previous,
          );
          clearPendingStripBanner();
        },
      });
    }

    return steps;
  };

  const saveAll = async () => {
    if (isSaving) return;
    const steps = buildSaveSteps();
    if (steps.length === 0) return;
    setIsSaving(true);
    try {
      const result = await runSaveSteps(steps);
      addNotificationItem(buildSaveResultMessage(result));
    } finally {
      setIsSaving(false);
    }
  };

  const revertAll = () => {
    if (!window.confirm("저장하지 않은 변경을 모두 되돌릴까요?")) return;
    setHeaderDesign(savedHeader);
    setFooterDesign(savedFooter);
    setSections(savedSections);
    setShopEntityData(savedShop);
    setProductsData((original) =>
      original
        ? original.map((product) => ({
            ...product,
            isBestSeller: savedBestSellerIds.includes(product.id),
          }))
        : original,
    );
    setBestSellerCount(savedBestSellerIds.length);
    clearPendingStripBanner();
  };

  /* ---------------------------- 미리보기 ---------------------------- */

  const shopBaseUrl = useMemo(
    () => toShopBaseUrl(companyInfo?.domain),
    [companyInfo?.domain],
  );

  /** 쇼핑몰 미리보기 서버가 받는 저장 전 설정. 상품은 ID 만 보낸다 */
  const previewDraft = useMemo(() => {
    if (!previewShopEntityData) return null;
    const shop = previewShopEntityData;
    return {
      layoutSpecification: {
        isCarouselLayout: shop.isCarouselLayout,
        designProductSlideInterval: shop.designProductSlideInterval,
        bestSellerEnabled: shop.bestSellerEnabled,
        recentlyViewedEnabled: shop.recentlyViewedEnabled,
        stripBannerEnabled: shop.stripBannerEnabled,
        stripBannerLink: shop.stripBannerLink ?? null,
        productDetailLayout: shop.productDetailLayout,
        myDesignEnabled: shop.myDesignEnabled,
        consultChannelType: shop.consultChannelType,
      },
      socialLink: {
        instagramUrl: shop.instagramUrl ?? null,
        facebookUrl: shop.facebookUrl ?? null,
        youtubeUrl: shop.youtubeUrl ?? null,
        naverBlogUrl: shop.naverBlogUrl ?? null,
        kakaoChannelUrl: shop.kakaoChannelUrl ?? null,
      },
      headerDesign,
      footerDesign,
      sections: effectiveSections.map((section) => ({
        id: section.id,
        sectionType: section.sectionType,
        isEnabled: section.isEnabled,
        title: section.title ?? null,
        subtitle: section.subtitle ?? null,
        cardShape: section.cardShape,
        columnCount: section.columnCount,
        layoutType: section.layoutType,
        productIds: section.products.map((product) => product.id),
      })),
      bestSellerIds: productsData ? bestSellerIdsOf(productsData) : undefined,
      hasPendingStripBanner: !!stripBannerFile,
      // 배너는 창에서 바로 저장되므로, 바뀌면 다시 그리게만 한다
      bannerVersion: (bannersData ?? [])
        .map((banner) => `${banner.id}:${banner.bannerUrl}`)
        .join("|"),
    };
  }, [
    previewShopEntityData,
    headerDesign,
    footerDesign,
    effectiveSections,
    productsData,
    stripBannerFile,
    bannersData,
  ]);

  /** 쇼핑몰 미리보기를 띄울 수 없을 때의 간이 미리보기 (예전 화면) */
  const renderLegacyPreviewSection = (section: ShopSectionEntity) => {
    if (!section.isEnabled) return null;
    switch (section.sectionType) {
      case "BANNER":
        return (
          <PreviewBanner
            shopEntityData={shopEntityData}
            bannersData={bannersData}
          />
        );
      case "STRIP_BANNER":
        return (
          <PreviewStripBanner
            shopEntityData={shopEntityData}
            stripBannerPreviewUrl={stripBannerPreviewUrl}
          />
        );
      case "RECENTLY_VIEWED":
        return (
          <PreviewRecentlyViewed
            recentlyViewedProducts={recentlyViewedProducts}
            title={section.title}
            subtitle={section.subtitle}
          />
        );
      case "BEST_SELLER":
        return (
          <PreviewBestSeller
            productsData={productsData}
            title={section.title}
            subtitle={section.subtitle}
          />
        );
      case "ALL_PRODUCTS":
        return (
          <PreviewAllProducts
            products={products}
            title={section.title}
            subtitle={section.subtitle}
            columnCount={section.columnCount}
          />
        );
      case "PORTFOLIO":
        return (
          <PreviewPortfolio
            section={section}
            fallbackPortfolios={publishedPortfolios}
          />
        );
      case "PRODUCT_LIST":
        return <PreviewProductList section={section} />;
      default:
        return null;
    }
  };

  const legacyPreview = (
    <div className="relative flex flex-col w-full bg-white">
      <PreviewConsultFloating
        shopEntityData={previewShopEntityData}
        variant="desktop"
      />
      <PreviewHeader
        companyLogoUrl={companyInfo?.logoUrl ?? ""}
        defaultLogoUrl={defaultLogoUrl}
        headerDesign={headerDesign}
        shopEntityData={previewShopEntityData}
        portfolioMenuEnabled={portfolioMenuEnabled}
      />
      {effectiveSections.map((section) => (
        <React.Fragment key={section.id}>
          {renderLegacyPreviewSection(section)}
        </React.Fragment>
      ))}
      <PreviewConsultFloating
        shopEntityData={previewShopEntityData}
        variant="mobile"
      />
      <PreviewFooter
        companyInfo={companyInfo}
        shopEntityData={shopEntityData}
        footerDesign={footerDesign}
      />
    </div>
  );

  /* ---------------------------- 화면 ---------------------------- */

  return (
    <>
      <div className="relative z-[500]">
        <NotificationsList notifications={notifications} />
      </div>

      <BestSellerModal
        setIsModalVisible={setIsBestSellerModalVisible}
        isModalVisible={isBestSellerModalVisible}
        productsData={productsData}
        setProductsData={setProductsData}
        bestSellerCount={bestSellerCount}
        setBestSellerCount={setBestSellerCount}
      />
      <NewBannerForm
        setIsModalVisible={setIsNewBannerModalVisible}
        isModalVisible={isNewBannerModalVisible}
        reloadBannersData={reloadBannerData}
      />
      <EditBannerForm
        setIsModalVisible={setIsEditBannerModalVisible}
        isModalVisible={isEditBannerModalVisible}
        reloadBannersData={reloadBannerData}
        bannersData={bannersData}
        setBannersData={setBannersData}
        editingBannerId={editingBannerId}
      />

      <CompanyAdminLayout>
        <div className="flex flex-col xl:flex-row gap-[1rem] p-[1rem] xl:h-full xl:min-h-0 text-[14px]">
          {/* 왼쪽: 실제 쇼핑몰 미리보기 */}
          <div className="flex-1 min-w-0 h-[75vh] xl:h-full">
            <ShopLivePreview
              shopBaseUrl={shopBaseUrl}
              page={previewPage}
              onPageChange={setPreviewPage}
              device={previewDevice}
              onDeviceChange={setPreviewDevice}
              draft={previewDraft}
              stripBannerDataUrl={stripBannerDataUrl}
              scrollRequest={scrollRequest}
              fallback={legacyPreview}
            />
          </div>

          {/* 오른쪽: 설정 (탭 · 카드 · 저장 바) */}
          <div className="flex flex-col w-full xl:w-[440px] xl:shrink-0 xl:h-full xl:min-h-0 text-start">
            {/* 탭 · 저장 바도 설정 목록과 같은 폭의 스크롤바 자리를 비워 오른쪽 끝을 맞춘다 */}
            <div className="shrink-0 overflow-hidden [scrollbar-gutter:stable] pr-[2px]">
              <div
                role="tablist"
                aria-label="쇼핑몰 디자인 설정"
                className="flex flex-row gap-[0.25rem] p-[0.25rem] mb-[0.75rem] bg-white border border-[#E5E7EB] rounded-[10px]"
              >
                {SETTINGS_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    onClick={() => showTab(tab.key)}
                    className={`relative flex-1 h-[2.25rem] rounded-[8px] text-[14px] transition-colors ${
                      activeTab === tab.key
                        ? "bg-[#E8EFFF] text-[#4285F4] font-bold"
                        : "text-[#555555] hover:text-[#252525]"
                    }`}
                  >
                    {tab.label}
                    {unsavedTabs.has(tab.key) ? (
                      <span
                        aria-label="저장하지 않은 변경"
                        className="absolute top-[0.4rem] right-[0.5rem] w-[6px] h-[6px] rounded-full bg-[#F24A4A]"
                      />
                    ) : null}
                  </button>
                ))}
              </div>
            </div>

            {/*
              탭을 바꿔도 카드는 마운트한 채로 두어 입력 중인 값이 남는다.
              섹션을 펼쳐 내용이 길어지면 스크롤바가 생기며 카드 폭이 줄어드는데, 스크롤바 자리를 미리 비워 두어 폭을 고정한다.
            */}
            <div
              ref={settingsScrollRef}
              className="flex-1 min-h-0 xl:overflow-y-auto [scrollbar-gutter:stable] pr-[2px]"
            >
              <div className={activeTab === "HEADER" ? "" : "hidden"}>
                <HeaderSettings
                  headerDesign={headerDesign}
                  setHeaderDesign={setHeaderDesign}
                />
              </div>
              <div className={activeTab === "MAIN" ? "" : "hidden"}>
                <MainSectionsSettings
                  sections={effectiveSections}
                  onReorder={setSections}
                  onToggleEnabled={toggleSectionEnabled}
                  onPatchSection={patchSection}
                  expandedSectionId={expandedSectionId}
                  onExpand={expandSection}
                  isPersistable={isSectionsPersistable}
                  shopEntityData={shopEntityData}
                  setShopEntityData={setShopEntityData}
                  bannersData={bannersData}
                  onAddBanner={() => setIsNewBannerModalVisible(true)}
                  onEditBanner={(bannerId) => {
                    setEditingBannerId(bannerId);
                    setIsEditBannerModalVisible(true);
                  }}
                  stripBannerPreviewUrl={stripBannerPreviewUrl}
                  onPickStripBanner={(file) => void pickStripBanner(file)}
                  onDeleteStripBanner={deleteStripBanner}
                  productsData={productsData}
                  visibleProductIds={
                    products
                      ? new Set(products.map((product) => product.id))
                      : null
                  }
                  onEditBestSellers={() => setIsBestSellerModalVisible(true)}
                  onCreateProductList={createProductListSection}
                  onDeleteProductList={deleteProductListSection}
                />
              </div>
              <div className={activeTab === "ORDER" ? "" : "hidden"}>
                <OrderSettings
                  shopEntityData={shopEntityData}
                  setShopEntityData={setShopEntityData}
                  liveConsult={liveConsult}
                  companyId={companyId}
                />
              </div>
              <div className={activeTab === "FOOTER" ? "" : "hidden"}>
                <FooterSettings
                  footerDesign={footerDesign}
                  setFooterDesign={setFooterDesign}
                  shopEntityData={shopEntityData}
                  setShopEntityData={setShopEntityData}
                />
              </div>
            </div>

            {/* 저장 바: 모든 탭의 변경을 한 번에 저장한다 */}
            <div className="shrink-0 overflow-hidden [scrollbar-gutter:stable] pr-[2px] pb-[2px]">
              <div className="mt-[0.75rem] flex flex-row items-center gap-[0.5rem] px-[1rem] py-[0.75rem] bg-white border border-[#E5E7EB] rounded-[10px] shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-bold">
                    {hasUnsavedChanges
                      ? "저장하지 않은 변경이 있습니다"
                      : "모든 변경이 저장되었습니다"}
                  </div>
                  <div
                    className={`text-[12px] truncate ${hasUnsavedChanges ? "text-[#F24A4A]" : "text-[#AEAEAE]"}`}
                  >
                    {hasUnsavedChanges
                      ? SETTINGS_TABS.filter((tab) => unsavedTabs.has(tab.key))
                          .map((tab) => tab.label)
                          .join(" · ")
                      : "미리보기는 저장 전 설정을 바로 보여 줍니다."}
                  </div>
                </div>
                {hasUnsavedChanges ? (
                  <Button
                    variant="ghost"
                    className="shrink-0 h-10"
                    disabled={isSaving}
                    onClick={revertAll}
                  >
                    되돌리기
                  </Button>
                ) : null}
                <Button
                  className="shrink-0 w-[5.5rem] h-10"
                  loading={isSaving}
                  disabled={isSaving || !hasUnsavedChanges}
                  onClick={saveAll}
                >
                  저장
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default MallDesign;
